/**
 * Building a world from typed data.
 *
 * `/content` is JSON; the content loader turns it into a `WorldBlueprint`
 * (typed, branded ids) and this turns a blueprint into a `WorldState`.
 * Nothing here draws from the PRNG yet: the seeded parts of generation
 * (district name, dormant situations, the vanished crew) are later prompts.
 * The PRNG is still seeded and stored so that when they arrive, a step stays
 * a pure function of the state.
 */

import { SUPPORTED_CONTENT_VERSION } from './content.js';
import { Rng, type Seedable } from './rng.js';
import type {
  Faction,
  Job,
  Location,
  NPC,
  PlayerCharacter,
  Secret,
  WorldState,
  WorldTime,
} from './types/index.js';

/** Bumped whenever a change alters the sequence of values drawn from a seed. */
export const SIM_VERSION = 1;

export interface WorldBlueprint {
  readonly seed: Seedable;
  readonly districtName: string;
  readonly bounds: WorldState['bounds'];
  readonly footprints: WorldState['footprints'];
  readonly start: WorldTime;
  readonly player: PlayerCharacter;
  readonly npcs: readonly NPC[];
  readonly locations: readonly Location[];
  readonly factions: readonly Faction[];
  readonly secrets: readonly Secret[];
  readonly jobs?: readonly Job[];
}

function byId<T extends { readonly id: string }>(items: readonly T[]): Record<string, T> {
  const out: Record<string, T> = {};
  for (const item of items) {
    if (out[item.id]) throw new Error(`Duplicate id in blueprint: ${item.id}`);
    out[item.id] = item;
  }
  return out;
}

export function createWorld(blueprint: WorldBlueprint): WorldState {
  const rng = new Rng(blueprint.seed);
  const locations = byId(blueprint.locations);
  const npcs = byId(blueprint.npcs);

  const { player } = blueprint;
  if (player.lodging !== null && !locations[player.lodging]) {
    throw new Error(`Player lodging ${player.lodging} is not a location`);
  }
  if (player.position.at !== null && !locations[player.position.at]) {
    throw new Error(`Player position ${player.position.at} is not a location`);
  }
  if (!npcs[player.dependent]) {
    throw new Error(`Player dependent ${player.dependent} is not an NPC`);
  }
  for (const npc of blueprint.npcs) {
    for (const slot of npc.routine) {
      if (!locations[slot.at]) throw new Error(`${npc.id} routine names unknown location ${slot.at}`);
    }
  }
  for (const job of blueprint.jobs ?? []) {
    if (!locations[job.at]) throw new Error(`${job.id} is at unknown location ${job.at}`);
    if (!npcs[job.employer]) throw new Error(`${job.id} names unknown employer ${job.employer}`);
  }

  return {
    seed: blueprint.seed,
    simVersion: SIM_VERSION,
    contentVersion: SUPPORTED_CONTENT_VERSION,
    rng: rng.getState(),
    nextId: 1,
    districtName: blueprint.districtName,
    bounds: blueprint.bounds,
    footprints: blueprint.footprints,
    time: blueprint.start,
    player,
    npcs,
    stubs: {},
    locations,
    factions: byId(blueprint.factions),
    secrets: byId(blueprint.secrets),
    grievances: {},
    appointments: {},
    situations: {},
    jobs: byId(blueprint.jobs ?? []),
    opening: null,
    scene: null,
    activeConversation: null,
    past: [],
    memory: { facts: {}, events: [], impressions: {}, texture: [] },
    economy: { priceIndex: 1, lastShock: null },
    hook: null,
  };
}
